// src/views/prestacaoContas/PrestacaoContasList.jsx
import React, { useState, useEffect, useCallback } from "react";
import prestacaoContasService from "../../services/prestacaoContasService";
import condominiosService     from "../../services/condominiosService";
import { useAuth }            from "../../contexts/AuthContext";
import Banner from "../../components/ui/Banner";
import ConfirmModal from "../../components/ui/ConfirmModal";

const fmtM  = v => Number(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtD  = d => { try{ return new Date(d+"T12:00:00").toLocaleDateString("pt-BR"); }catch{ return d||"—"; } };
const fmtDt = d => { try{ return new Date((String(d||"").split("T")[0])+"T12:00:00").toLocaleDateString("pt-BR"); }catch{ return d||"—"; } };
const primeiroDia = (dt=new Date()) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-01`;
const ultimoDia   = (dt=new Date()) => new Date(dt.getFullYear(),dt.getMonth()+1,0).toISOString().split("T")[0];

export default function PrestacaoContasList() {
  const { condominioAtivo } = useAuth();
  const [condominios, setCondominios] = useState([]);
  const [idCondo, setIdCondo]     = useState("");
  const [aba, setAba]             = useState("gerar");
  const [dtIni, setDtIni]         = useState(primeiroDia());
  const [dtFim, setDtFim]         = useState(ultimoDia());
  const [titulo, setTitulo]       = useState("");
  const [preview, setPreview]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [printing, setPrinting]   = useState(false);
  const [historico, setHistorico] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [showEnviar, setShowEnviar]   = useState(false);
  const [enviarId, setEnviarId]       = useState(null);
  const [canal, setCanal]             = useState("ambos");
  const [enviando, setEnviando]       = useState(false);
  const [resultEnvio, setResultEnvio] = useState(null);

  // UX: banner + confirmacao
  const [banner, setBanner] = useState(null);
  const [confirmacao, setConfirmacao] = useState(null);
  const notify = useCallback((tipo, msg) => {
    setBanner({ tipo, msg });
    if (tipo === "success" || tipo === "info") {
      setTimeout(() => setBanner((b) => (b && b.msg === msg ? null : b)), 4000);
    }
  }, []);
  const askConfirm = useCallback(
    (cfg) => new Promise((resolve) => {
      setConfirmacao({
        titulo: cfg.titulo || "Confirmar",
        mensagem: cfg.mensagem,
        perigo: !!cfg.perigo,
        textoOk: cfg.textoOk || "Confirmar",
        onConfirm: () => { setConfirmacao(null); resolve(true); },
        onCancel:  () => { setConfirmacao(null); resolve(false); },
      });
    }),
    []
  );

  useEffect(() => {
    condominiosService.listar({limit:100}).then(r=>setCondominios(r.data||[])).catch(()=>{});
  }, []);

  // Sync with navbar condominioAtivo — updates whenever user changes it
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  const carregarHistorico = useCallback(async () => {
    if (!idCondo) return;
    setLoadingHist(true);
    try { const r = await prestacaoContasService.listar({idCondominio:idCondo}); setHistorico(r.data||[]); }
    catch {}
    setLoadingHist(false);
  }, [idCondo]);

  useEffect(() => { if (aba==="historico") carregarHistorico(); }, [aba, carregarHistorico]);

  const handlePreview = async () => {
    if (!idCondo||!dtIni||!dtFim) return notify("warning","Preencha todos os campos.");
    setLoading(true); setPreview(null);
    try { const r = await prestacaoContasService.preview({idCondominio:idCondo,dtIni,dtFim}); setPreview(r.data); }
    catch(e) { notify("error", e.message); }
    setLoading(false);
  };

  const handleGerar = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      await prestacaoContasService.criar({idCondominio:idCondo,dtIni,dtFim,titulo});
      notify("success", "Prestação de contas gerada!");
      setPreview(null); setAba("historico");
    } catch(e) { notify("error", e.message); }
    setSaving(false);
  };

  const handleImprimir = async (id) => {
    setPrinting(true);
    try {
      const r = await prestacaoContasService.imprimir(id);
      const w = window.open("", "_blank", "width=900,height=820");
      if (!w) {
        notify("warning", "Pop-up bloqueado. Permita pop-ups deste site para imprimir.");
      } else {
        w.document.write(r.html); w.document.close();
      }
    }
    catch(e) { notify("error", e.message); }
    setPrinting(false);
  };

  const handlePublicar = async (id) => {
    const ok = await askConfirm({
      titulo: "Publicar prestação de contas",
      mensagem: "Publicar como Comunicado? Todos os condôminos verão no sistema. Esta ação não pode ser desfeita.",
      textoOk: "Publicar",
    });
    if (!ok) return;
    try {
      const r = await prestacaoContasService.publicar(id);
      notify("success", r.message);
      carregarHistorico();
    } catch(e) { notify("error", e.message); }
  };

  const handleEnviar = async () => {
    setEnviando(true);
    try { const r = await prestacaoContasService.enviar(enviarId, canal); setResultEnvio(r); carregarHistorico(); }
    catch(e) { setResultEnvio({success:false,message:e.message}); }
    setEnviando(false);
  };

  const corNome = condominios.find(c=>String(c.id)===String(idCondo))?.nmFantasia || "";

  return (
    <div>
      <div className="page-header" style={{marginBottom:20}}>
        <div>
          <h1 className="page-title">
            <i className="bi bi-clipboard-data me-2" style={{color:"var(--primary)",fontSize:22}}/>
            Prestação de Contas
          </h1>
          <p className="page-subtitle">Gere e distribua relatórios financeiros para os condôminos</p>
        </div>
        
      </div>

      {/* Abas */}
      <div style={{display:"flex",gap:4,marginBottom:16,borderBottom:"1px solid var(--gray-200)"}}>
        {[{id:"gerar",icon:"bi-plus-circle",label:"Gerar Nova"},{id:"historico",icon:"bi-clock-history",label:"Histórico"}].map(t=>(
          <button key={t.id} onClick={()=>setAba(t.id)}
            style={{padding:"8px 16px",border:"none",background:"transparent",cursor:"pointer",fontSize:13,
              fontWeight:aba===t.id?700:400,color:aba===t.id?"var(--primary)":"var(--gray-500)",
              borderBottom:aba===t.id?"2px solid var(--primary)":"2px solid transparent",marginBottom:-1}}>
            <i className={`bi ${t.icon} me-1`}/>{t.label}
          </button>
        ))}
      </div>

      {/* ══ GERAR ══ */}
      {aba==="gerar" && (
        <div style={{display:"grid",gridTemplateColumns:"300px 1fr",gap:16}}>
          <div style={{background:"var(--bg-surface)",borderRadius:12,padding:20,border:"1px solid var(--gray-200)",alignSelf:"start"}}>
            <div style={{fontWeight:700,fontSize:14,marginBottom:16,color:"var(--gray-800)"}}>
              <i className="bi bi-funnel me-2" style={{color:"var(--primary)"}}/>Configurar Período
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div><label className="form-label">Data Inicial</label>
                <input type="date" className="form-control" value={dtIni} onChange={e=>{setDtIni(e.target.value);setPreview(null);}}/></div>
              <div><label className="form-label">Data Final</label>
                <input type="date" className="form-control" value={dtFim} onChange={e=>{setDtFim(e.target.value);setPreview(null);}}/></div>
              <div>
                <label className="form-label" style={{fontSize:11}}>Atalhos</label>
                <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                  {[
                    {l:"Mês atual",    f:()=>{setDtIni(primeiroDia());setDtFim(ultimoDia());}},
                    {l:"Mês anterior", f:()=>{const d=new Date();d.setMonth(d.getMonth()-1);setDtIni(primeiroDia(d));setDtFim(ultimoDia(d));}},
                    {l:"Trimestre",    f:()=>{const d=new Date();const q=Math.floor(d.getMonth()/3);setDtIni(`${d.getFullYear()}-${String(q*3+1).padStart(2,"0")}-01`);const mf=q*3+3;setDtFim(new Date(d.getFullYear(),mf,0).toISOString().split("T")[0]);}},
                    {l:"Semestre",     f:()=>{const d=new Date();const s=d.getMonth()<6?0:6;setDtIni(`${d.getFullYear()}-${String(s+1).padStart(2,"0")}-01`);setDtFim(new Date(d.getFullYear(),s+6,0).toISOString().split("T")[0]);}},
                    {l:"Ano",          f:()=>{const a=new Date().getFullYear();setDtIni(`${a}-01-01`);setDtFim(`${a}-12-31`);}},
                  ].map(a=>(
                    <button key={a.l} className="btn btn-outline-secondary btn-sm" style={{fontSize:11,padding:"2px 8px"}}
                      onClick={()=>{a.f();setPreview(null);}}>{a.l}</button>
                  ))}
                </div>
              </div>
              <div><label className="form-label">Título (opcional)</label>
                <input className="form-control" value={titulo} onChange={e=>setTitulo(e.target.value)}
                  placeholder="Ex: Prestação de Contas — Jan/2026"/></div>
              <button className="btn btn-primary w-100" onClick={handlePreview} disabled={loading||!idCondo}>
                {loading?<><span className="spinner-border spinner-border-sm me-2"/>Carregando...</>:<><i className="bi bi-eye me-2"/>Visualizar Preview</>}
              </button>
            </div>
          </div>

          {/* Preview */}
          <div>
            {!preview&&!loading&&(
              <div style={{textAlign:"center",padding:"80px 0",color:"var(--gray-400)",background:"var(--bg-surface)",borderRadius:12,border:"1px solid var(--gray-200)"}}>
                <i className="bi bi-clipboard-data" style={{fontSize:48,display:"block",marginBottom:12}}/>
                <p style={{fontSize:15}}>Configure o período e clique em <strong>Visualizar Preview</strong></p>
              </div>
            )}
            {preview&&(
              <div style={{background:"var(--bg-surface)",borderRadius:12,border:"1px solid var(--gray-200)",overflow:"hidden"}}>
                <div style={{background:"#1B3A6B",color:"#fff",padding:"16px 20px"}}>
                  <div style={{fontSize:16,fontWeight:700}}>📊 PRESTAÇÃO DE CONTAS</div>
                  <div style={{fontSize:12,opacity:.8,marginTop:4}}>{corNome} · {fmtD(dtIni)} a {fmtD(dtFim)}</div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:0,borderBottom:"1px solid var(--gray-100)"}}>
                  {[
                    {l:"Receitas",      v:fmtM(preview.receitas.total),   c:"#166534",bg:"#DCFCE7"},
                    {l:"Despesas",      v:fmtM(preview.despesas.total),   c:"#991B1B",bg:"#FEE2E2"},
                    {l:preview.saldoMes>=0?"Superávit":"Déficit", v:fmtM(Math.abs(preview.saldoMes)), c:preview.saldoMes>=0?"#166534":"#991B1B", bg:preview.saldoMes>=0?"#DCFCE7":"#FEE2E2"},
                    {l:"Saldo Bancário",v:fmtM(preview.saldoBancario),    c:"#1D4ED8",bg:"#EFF6FF"},
                  ].map(s=>(
                    <div key={s.l} style={{padding:"16px 14px",textAlign:"center",background:s.bg}}>
                      <div style={{fontSize:18,fontWeight:700,color:s.c}}>{s.v}</div>
                      <div style={{fontSize:11,color:s.c,marginTop:3}}>{s.l}</div>
                    </div>
                  ))}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:0}}>
                  <div style={{padding:16,borderRight:"1px solid var(--gray-100)"}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#166534",marginBottom:10,textTransform:"uppercase"}}>Receitas</div>
                    <div style={{fontSize:13,display:"flex",justifyContent:"space-between",padding:"4px 0"}}>
                      <span>Cota Condominial</span><span style={{fontWeight:600}}>{fmtM(preview.receitas.cobrancas)}</span>
                    </div>
                    {preview.receitas.outras>0&&<div style={{fontSize:13,display:"flex",justifyContent:"space-between",padding:"4px 0"}}>
                      <span>Outras Receitas</span><span style={{fontWeight:600}}>{fmtM(preview.receitas.outras)}</span>
                    </div>}
                    <div style={{display:"flex",justifyContent:"space-between",padding:"8px 10px",background:"var(--bg-success-soft)",borderRadius:6,marginTop:8,fontWeight:700,color:"#166534"}}>
                      <span>TOTAL</span><span>{fmtM(preview.receitas.total)}</span>
                    </div>
                  </div>
                  <div style={{padding:16}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#991B1B",marginBottom:10,textTransform:"uppercase"}}>Maiores Despesas</div>
                    {preview.despesas.top5.length===0?<p style={{fontSize:12,color:"var(--gray-400)"}}>Nenhuma despesa</p>
                    :preview.despesas.top5.map((t,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",fontSize:13,borderBottom:"1px solid var(--gray-50)"}}>
                        <span style={{color:"var(--gray-600)"}}>{t.descConta||"Outras"}</span>
                        <span style={{fontWeight:600}}>{fmtM(t.vlr)}</span>
                      </div>
                    ))}
                    <div style={{display:"flex",justifyContent:"space-between",padding:"8px 10px",background:"var(--bg-danger-soft)",borderRadius:6,marginTop:8,fontWeight:700,color:"#991B1B"}}>
                      <span>TOTAL</span><span>{fmtM(preview.despesas.total)}</span>
                    </div>
                  </div>
                </div>
                <div style={{padding:"12px 16px",background:"var(--gray-50)",borderTop:"1px solid var(--gray-100)",fontSize:13}}>
                  <i className="bi bi-exclamation-circle me-2" style={{color:"#C2410C"}}/>
                  <strong>Inadimplência:</strong> {preview.inadimplencia.qtd} unidade(s) ({preview.inadimplencia.perc}%) · {fmtM(preview.inadimplencia.vlr)}
                </div>
                <div style={{padding:"14px 16px",borderTop:"1px solid var(--gray-100)",display:"flex",gap:8,justifyContent:"flex-end"}}>
                  <button className="btn btn-outline-secondary" onClick={()=>setPreview(null)}>
                    <i className="bi bi-arrow-left me-1"/>Ajustar
                  </button>
                  <button className="btn btn-primary" onClick={handleGerar} disabled={saving}>
                    {saving?<><span className="spinner-border spinner-border-sm me-2"/>Gerando...</>:<><i className="bi bi-save me-2"/>Salvar Prestação de Contas</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ HISTÓRICO ══ */}
      {aba==="historico"&&(
        <div style={{background:"var(--bg-surface)",borderRadius:12,border:"1px solid var(--gray-200)",overflow:"hidden"}}>
          <div style={{padding:"14px 18px",borderBottom:"1px solid var(--gray-100)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontWeight:700,fontSize:14}}>Histórico de Prestações de Contas</span>
            <button className="btn btn-outline-secondary btn-sm" onClick={carregarHistorico}><i className="bi bi-arrow-clockwise"/></button>
          </div>
          {loadingHist?<div style={{textAlign:"center",padding:40}}><span className="spinner-border text-primary"/></div>
          :historico.length===0?<div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
            <i className="bi bi-clipboard-data" style={{fontSize:32,display:"block",marginBottom:8}}/>
            <p>Nenhuma prestação de contas gerada</p>
            <button className="btn btn-primary btn-sm mt-2" onClick={()=>setAba("gerar")}><i className="bi bi-plus me-1"/>Gerar Agora</button>
          </div>:(
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead><tr style={{background:"var(--gray-50)"}}>
                {["Título","Período","Status","Canal","Criado em","Ações"].map(h=>(
                  <th key={h} style={{padding:"10px 14px",textAlign:"left",fontWeight:600,color:"var(--gray-600)",fontSize:12}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {historico.map((h,i)=>(
                  <tr key={h.id} style={{borderBottom:"1px solid var(--gray-100)",background:i%2===0?"#fff":"var(--gray-50)"}}>
                    <td style={{padding:"10px 14px",fontWeight:600,fontSize:13}}>{h.titulo}</td>
                    <td style={{padding:"10px 14px",fontSize:12,color:"var(--gray-500)"}}>{fmtD(h.dtIni)} a {fmtD(h.dtFim)}</td>
                    <td style={{padding:"10px 14px"}}>
                      <span style={{background:h.status==="Publicado"?"#DCFCE7":"#FEF9C3",color:h.status==="Publicado"?"#166534":"#854D0E",fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:10}}>{h.status}</span>
                    </td>
                    <td style={{padding:"10px 14px",fontSize:12}}>
                      {h.enviouEmail&&<i className="bi bi-envelope-fill me-1" style={{color:"#1D4ED8"}} title="E-mail"/>}
                      {h.enviouWhatsapp&&<i className="bi bi-whatsapp" style={{color:"#25D366"}} title="WhatsApp"/>}
                      {!h.enviouEmail&&!h.enviouWhatsapp&&<span style={{color:"var(--gray-400)"}}>—</span>}
                    </td>
                    <td style={{padding:"10px 14px",fontSize:12,color:"var(--gray-500)"}}>{fmtDt(h.createdAt)}</td>
                    <td style={{padding:"10px 14px"}}>
                      <div style={{display:"flex",gap:4}}>
                        <button className="btn btn-outline-secondary btn-sm btn-icon" title="Imprimir" onClick={()=>handleImprimir(h.id)} disabled={printing}><i className="bi bi-printer"/></button>
                        {h.status==="Rascunho"&&<button className="btn btn-outline-primary btn-sm btn-icon" title="Publicar como Comunicado" onClick={()=>handlePublicar(h.id)}><i className="bi bi-megaphone"/></button>}
                        <button className="btn btn-outline-success btn-sm btn-icon" title="Enviar por E-mail/WhatsApp" onClick={()=>{setEnviarId(h.id);setResultEnvio(null);setShowEnviar(true);}}><i className="bi bi-send-fill"/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal Envio */}
      {showEnviar&&(
        <div className="modal-overlay" onClick={()=>!enviando&&setShowEnviar(false)}>
          <div className="modal-box" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-send-fill me-2" style={{color:"#25D366"}}/>Enviar Prestação de Contas</h5>
              <button className="btn-close" onClick={()=>setShowEnviar(false)} disabled={enviando}/>
            </div>
            <div className="modal-body">
              {resultEnvio?(
                <div style={{textAlign:"center",padding:"16px 0"}}>
                  <div style={{width:60,height:60,borderRadius:"50%",margin:"0 auto 12px",background:resultEnvio.success?"#DCFCE7":"#FEE2E2",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <i className={`bi ${resultEnvio.success?"bi-check-circle-fill":"bi-x-circle-fill"}`} style={{fontSize:28,color:resultEnvio.success?"#166534":"#991B1B"}}/>
                  </div>
                  <div style={{fontWeight:700,fontSize:15,color:resultEnvio.success?"#166534":"#991B1B"}}>{resultEnvio.message}</div>
                  {resultEnvio.enviados!==undefined&&<div style={{marginTop:8,fontSize:13,color:"var(--gray-500)"}}>✅ {resultEnvio.enviados} enviado(s){resultEnvio.erros>0&&<span style={{color:"#991B1B"}}> · ❌ {resultEnvio.erros} erro(s)</span>}</div>}
                </div>
              ):(
                <>
                  <p style={{fontSize:13,color:"var(--gray-600)",marginBottom:16}}>Será enviada para <strong>todos os condôminos</strong> com contato cadastrado.</p>
                  <div style={{display:"flex",gap:8}}>
                    {[{v:"email",icon:"bi-envelope-fill",l:"E-mail",c:"#1D4ED8",bg:"#EFF6FF"},{v:"whatsapp",icon:"bi-whatsapp",l:"WhatsApp",c:"#166534",bg:"#DCFCE7"},{v:"ambos",icon:"bi-send-fill",l:"Ambos",c:"#7C3AED",bg:"#F5F3FF"}].map(op=>(
                      <button key={op.v} onClick={()=>setCanal(op.v)}
                        style={{flex:1,padding:"10px 8px",borderRadius:8,cursor:"pointer",border:`2px solid ${canal===op.v?op.c:"var(--gray-200)"}`,background:canal===op.v?op.bg:"#fff",color:canal===op.v?op.c:"var(--gray-500)",fontWeight:canal===op.v?700:400,fontSize:13,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                        <i className={`bi ${op.icon}`} style={{fontSize:18}}/>{op.l}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              {resultEnvio?<button className="btn btn-outline-secondary" onClick={()=>setShowEnviar(false)}>Fechar</button>:(
                <>
                  <button className="btn btn-outline-secondary" onClick={()=>setShowEnviar(false)} disabled={enviando}>Cancelar</button>
                  <button className="btn btn-success" onClick={handleEnviar} disabled={enviando}>
                    {enviando?<><span className="spinner-border spinner-border-sm me-2"/>Enviando...</>:<><i className="bi bi-send-fill me-2"/>Enviar para Todos</>}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {banner && (
        <div style={{ position:"fixed", top:16, right:16, zIndex:9999, maxWidth:480 }}>
          <Banner tipo={banner.tipo} onClose={()=>setBanner(null)}>{banner.msg}</Banner>
        </div>
      )}
      {confirmacao && (
        <ConfirmModal
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          perigo={confirmacao.perigo}
          textoOk={confirmacao.textoOk}
          onConfirm={confirmacao.onConfirm}
          onCancel={confirmacao.onCancel}
        />
      )}
    </div>
  );
}
